import { CssTransition, type CssTransitionProps } from '@mui/base'
import {
  TransitionContext,
  useTransitionTrigger,
} from '@mui/base/useTransition'
import { clsx } from 'clsx'
import {
  forwardRef,
  type PropsWithChildren,
  useDeferredValue,
  useEffect,
} from 'react'

import styles from './DrawerTransition.module.css'

type DrawerTransitionProps = CssTransitionProps &
  PropsWithChildren<{
    direction?: 'left'
    in: boolean
    onEnter?: () => void
    onExited?: () => void
  }>

export const DrawerTransition = forwardRef<
  HTMLDivElement,
  DrawerTransitionProps
>(function ProductDrawerTransition(props, ref) {
  const {
    children,
    className,
    direction,
    in: open,
    onEnter,
    onExited,
    ...rest
  } = props

  const { contextValue, hasExited } = useTransitionTrigger(open)

  /*
   * This is workaround for bug https://github.com/mui/material-ui/issues/40894.
   * Issue happens as `hasExited` changes too late and `onExited` is executed before animation starts.
   */
  const deferredHasExited = useDeferredValue(hasExited)

  useEffect(() => {
    if (contextValue.requestedEnter) {
      onEnter?.()
    }
  }, [contextValue.requestedEnter, onEnter])

  useEffect(() => {
    if (deferredHasExited) {
      onExited?.()
    }
  }, [deferredHasExited, onExited])

  return (
    <TransitionContext.Provider value={contextValue}>
      <CssTransition
        className={clsx(
          !direction && styles['drawer-transition'],
          direction === 'left' && styles['drawer-transition-left'],
          className,
        )}
        enterClassName={clsx(
          !direction && styles['drawer-transition_open'],
          direction === 'left' && styles['drawer-transition-left_open'],
        )}
        ref={ref}
        {...rest}
      >
        {children}
      </CssTransition>
    </TransitionContext.Provider>
  )
})
